// src/components/Pneumonia.js
import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { deductCredits } from '../utils/credits';
import ReportTemplate from '../utils/ReportTemplate';
import Chart from 'chart.js/auto';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const Pneumonia = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [result, setResult] = useState('');
  const [mappedResult, setMappedResult] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [labelName, setLabelName] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isFileHovering, setIsFileHovering] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const imageRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const fetchApiSettings = async () => {
      try {
        const apiSettingsRef = doc(db, 'settings', 'api');
        const apiSettingsSnap = await getDoc(apiSettingsRef);
        if (apiSettingsSnap.exists()) {
          const data = apiSettingsSnap.data();
          setApiUrl(data.pneumoniaApi?.url || 'https://xray-model-service-1029646538571.us-central1.run.app/predict');
        } else {
          setApiUrl('https://xray-model-service-1029646538571.us-central1.run.app/predict');
        }
      } catch (err) {
        console.error('Error loading API settings:', err);
        setApiUrl('https://xray-model-service-1029646538571.us-central1.run.app/predict');
      }
    };
    fetchApiSettings();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate, auth]);

  useEffect(() => {
    // Create chart when results are shown
    if (showResults && chartRef.current) {
      // Destroy existing chart if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      
      // Prepare data for chart
      const data = {
        labels: ['Pneumonia', 'Normal'],
        datasets: [{
          label: 'Probability (%)',
          data: [
            mappedResult === 'Pneumonia' ? confidence : 100 - confidence,
            mappedResult === 'Normal' ? confidence : 100 - confidence
          ],
          backgroundColor: [
            'rgba(244, 67, 54, 0.7)',
            'rgba(76, 175, 80, 0.7)'
          ],
          borderColor: [
            'rgba(244, 67, 54, 1)',
            'rgba(76, 175, 80, 1)'
          ],
          borderWidth: 1
        }]
      };

      // Create new chart
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: data,
        options: {
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.formattedValue}%`;
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              max: 100,
              title: {
                display: true,
                text: 'Probability (%)'
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    }
  }, [mappedResult, confidence, showResults]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsFileHovering(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const processFile = (selectedFile) => {
    setFile(selectedFile);
    setResult('');
    setMappedResult('');
    setError('');
    setConfidence(0);
    setShowResults(false);

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewUrl(event.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handlePrediction = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!file) {
      setError('Please select an image file.');
      return;
    }
    if (!labelName.trim()) {
      setError('Please enter a label name.');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      await deductCredits(user.uid, 10);

      // Upload image to Firebase Storage
      const storageRef = ref(storage, `medical_images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      const bucketPath = `gs://${storage.app.options.storageBucket}/${storageRef.fullPath}`;

      console.log('Image uploaded successfully:', { imageUrl, bucketPath });

      // Send the image to the prediction API
      console.log('Sending to prediction API:', apiUrl);
      const predictionFormData = new FormData();
      predictionFormData.append('file', file);
      predictionFormData.append('bucket_path', bucketPath);
      
      const predictionResponse = await fetch(apiUrl, {
        method: 'POST',
        body: predictionFormData,
      });

      if (!predictionResponse.ok) {
        const responseText = await predictionResponse.text();
        console.error('Prediction failed with status:', predictionResponse.status);
        console.error('Response text:', responseText);
        
        let errorMessage = 'Failed to get prediction';
        try {
          if (responseText.trim().startsWith('{')) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Prediction failed (${predictionResponse.status}): ${responseText.slice(0, 100)}`;
          }
        } catch (e) {
          console.error('Error parsing API error response:', e);
          errorMessage = `Prediction failed (${predictionResponse.status})`;
        }
        throw new Error(errorMessage);
      }

      const data = await predictionResponse.json();
      
      // Finish progress animation
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Set results
      const mapped = data.prediction === 'PNEUMONIA' ? 'Pneumonia' : 'Normal';
      setResult(data.prediction);
      setMappedResult(mapped);
      setConfidence(data.confidence * 100);
      setShowResults(true);

      // Save prediction to history
      await addDoc(collection(db, 'prediction_history'), {
        userId: user.uid,
        result: data.prediction,
        mappedResult: mapped,
        confidence: data.confidence * 100,
        date: new Date().toISOString(),
        imageUrl,
        bucketPath,
        type: 'X-Ray',
        name: user.email || 'Anonymous',
        labelName: labelName.trim(),
      });

      // Update prediction counts
      const countsRef = doc(db, 'prediction_counts', 'counts');
      const countsSnap = await getDoc(countsRef);
      if (countsSnap.exists()) {
        const currentCounts = countsSnap.data();
        await updateDoc(countsRef, {
          ...(mapped === 'Pneumonia' ? { xrayPneumonia: currentCounts.xrayPneumonia + 1 } : { xrayNormal: currentCounts.xrayNormal + 1 }),
        });
        console.log('Prediction counts updated successfully');
      } else {
        console.warn('Prediction counts document does not exist, creating it');
        await updateDoc(countsRef, {
          xrayPneumonia: mapped === 'Pneumonia' ? 1 : 0,
          xrayNormal: mapped === 'Normal' ? 1 : 0,
        });
      }

      // Clear the labelName input after successful submission
      setLabelName('');
    } catch (err) {
      console.error('Prediction error:', err);
      setError(err.message);
    } finally {
      clearInterval(progressInterval);
      setUploadProgress(100);
      setLoading(false);
    }
  };

  const downloadReportAsPDF = () => {
    // Clinical description based on findings
    const clinicalDescription = mappedResult === 'Pneumonia' 
      ? "The chest x-ray shows features consistent with pneumonia, characterized by patchy or diffuse opacities in the lung fields. These findings suggest the presence of inflammatory exudate in the alveolar spaces. Further clinical correlation is recommended."
      : "The chest x-ray appears normal. Lung fields are clear without evidence of consolidation, effusion, or pneumothorax. Heart size is within normal limits. No visible acute pulmonary disease.";
    
    // Recommendations based on findings
    const recommendations = mappedResult === 'Pneumonia' 
      ? [
          "Clinical correlation with patient symptoms and laboratory findings",
          "Consider appropriate antibiotic therapy based on clinical assessment",
          "Follow-up imaging may be needed to monitor response to treatment",
          "Consider additional diagnostic tests if clinically indicated"
        ] 
      : [
          "No specific action required based on this x-ray",
          "If patient has persistent symptoms, clinical reassessment is recommended",
          "Follow standard clinical protocols based on patient's presenting symptoms"
        ];
    
    // Probabilities for the report
    const probabilities = {
      'Pneumonia': mappedResult === 'Pneumonia' ? confidence/100 : (100-confidence)/100,
      'Normal': mappedResult === 'Normal' ? confidence/100 : (100-confidence)/100
    };
    
    // Generate the standardized report using the template
    ReportTemplate.generateReport({
      reportType: 'xray',
      patientId: labelName || 'Unknown',
      finding: result,
      confidence: confidence,
      probabilities: probabilities,
      description: clinicalDescription,
      recommendations: recommendations,
      imageRef: imageRef.current,
      imageUrl: previewUrl
    });
  };

  const getResultColor = (result) => {
    return result === 'Pneumonia' ? 'rgb(244, 67, 54)' : 'rgb(76, 175, 80)';
  };

  return (
    <div className="pneumonia min-h-screen font-['Noto Sans'] pt-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Pneumonia X-Ray Analysis</h1>
            <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
              Cost: 10 Credits
            </div>
          </div>
          <p className="text-gray-600 mt-2">
            Upload a chest X-ray image to detect pneumonia using advanced AI technology.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Upload X-Ray Image</h2>
            <form onSubmit={handlePrediction} className="space-y-4">
              <div>
                <label htmlFor="labelName" className="block text-sm font-medium text-gray-700 mb-1">
                  Label/Patient ID
                </label>
                <input
                  id="labelName"
                  type="text"
                  placeholder="Enter label or patient ID"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <div 
                className={`mt-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isFileHovering ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-300'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsFileHovering(true);
                }}
                onDragLeave={() => setIsFileHovering(false)}
                onDrop={handleDrop}
              >
                {previewUrl ? (
                  <div className="space-y-4">
                    <img
                      src={previewUrl}
                      alt="X-Ray Preview"
                      className="max-h-48 mx-auto rounded"
                      ref={imageRef}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setPreviewUrl(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove image
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="text-sm text-gray-600">
                      Drag and drop an X-ray image, or
                      <label className="relative cursor-pointer rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500 ml-1">
                        <span>browse</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                )}
              </div>
              
              <button
                type="submit"
                disabled={loading || !file}
                className={`w-full px-4 py-2 rounded-md font-medium text-white transition-colors ${
                  loading || !file
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                }`}
              >
                {loading ? 'Processing...' : 'Analyze X-Ray'}
              </button>
              
              {loading && (
                <div className="mt-4">
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block text-purple-600">
                          {uploadProgress < 100 ? 'Uploading & Processing' : 'Analysis Complete'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-purple-600">
                          {uploadProgress}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-purple-200">
                      <div 
                        style={{ width: `${uploadProgress}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500 transition-all duration-500"
                      ></div>
                    </div>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6 h-full">
              {!showResults ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-lg font-medium">No Analysis Results Yet</p>
                  <p className="mt-2 text-sm text-center">Upload an X-ray and click "Analyze X-Ray" to see the results here.</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-700">Analysis Results</h2>
                    <button
                      onClick={downloadReportAsPDF}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Report
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-6">
                        <div className="flex items-center">
                          <div 
                            className={`w-4 h-4 rounded-full mr-2`}
                            style={{ backgroundColor: getResultColor(mappedResult) }}
                          ></div>
                          <h3 className="text-lg font-medium text-gray-900">Prediction</h3>
                        </div>
                        <p className="mt-1 text-2xl font-bold" style={{ color: getResultColor(mappedResult) }}>
                          {result}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Confidence Score: {confidence.toFixed(2)}%
                        </p>
                      </div>

                      {/* Additional information for pneumonia */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h3 className="font-medium text-gray-900">Information</h3>
                        <p className="mt-2 text-sm text-gray-600">
                          {mappedResult === 'Pneumonia' ? 
                            "Pneumonia is an infection that inflames the air sacs in one or both lungs. The air sacs may fill with fluid or pus, causing cough with phlegm, fever, chills, and difficulty breathing." : 
                            "The X-ray appears normal with no signs of pneumonia. Healthy lungs appear clear without fluid build-up or significant opacities on an X-ray."
                          }
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Probability Distribution</h3>
                      <div className="h-64 relative">
                        <canvas ref={chartRef}></canvas>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500">IMPORTANT DISCLAIMER</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      The results provided by this AI system are intended for informational purposes only and should not be considered as a medical diagnosis. 
                      Even with high accuracy levels, all results should be reviewed by a qualified medical professional. 
                      Always consult with a healthcare provider for proper diagnosis and treatment decisions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pneumonia;